import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useBuilderStore } from '../../stores/builderStore';
import i18n from '../../i18n';

interface FormSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function FormSettingsModal({ isOpen, onClose }: FormSettingsModalProps) {
  const { t } = useTranslation(['builder', 'common']);
  const { formSchema, updateSettings } = useBuilderStore();
  const isRTL = i18n.language === 'he' || i18n.language === 'ar';

  const [direction, setDirection] = useState<'ltr' | 'rtl'>(formSchema.settings.direction || 'ltr');
  const [subtitle, setSubtitle] = useState(formSchema.settings.subtitle || '');
  const [instructions, setInstructions] = useState(formSchema.settings.instructions || '');
  const [submitButtonText, setSubmitButtonText] = useState(formSchema.settings.submitButtonText || 'Submit');
  const [successMessage, setSuccessMessage] = useState(formSchema.settings.successMessage || 'Thank you for your submission!');
  const [redirectUrl, setRedirectUrl] = useState(formSchema.settings.redirectUrl || '');
  const [logo, setLogo] = useState(formSchema.settings.logo || '');
  const [logoInputType, setLogoInputType] = useState<'url' | 'upload'>('url');
  const [favicon, setFavicon] = useState(formSchema.settings.favicon || '');
  const [faviconInputType, setFaviconInputType] = useState<'url' | 'upload'>('upload');

  // Form Access Control state (for accessing the form itself)
  const [accessControlEnabled, setAccessControlEnabled] = useState(formSchema.settings.accessControl?.enabled || false);
  const [accessMethod, setAccessMethod] = useState<'password' | 'multi_password' | 'user_password' | 'email_otp'>(
    formSchema.settings.accessControl?.method || 'password'
  );
  const [accessPassword, setAccessPassword] = useState('');
  const [accessPasswords, setAccessPasswords] = useState<string[]>(formSchema.settings.accessControl?.passwords || []);
  const [accessCredentials, setAccessCredentials] = useState<Array<{ username: string; passwordHash: string }>>(
    formSchema.settings.accessControl?.credentials || []
  );
  const [accessEmails, setAccessEmails] = useState<string[]>(formSchema.settings.accessControl?.allowedEmails || []);
  const [accessTitle, setAccessTitle] = useState(formSchema.settings.accessControl?.accessTitle || '');
  const [accessMessage, setAccessMessage] = useState(formSchema.settings.accessControl?.accessMessage || '');
  const [newPassword, setNewPassword] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newUsername, setNewUsername] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');

  // Results Access Control state (for viewing results)
  const [resultsAccessEnabled, setResultsAccessEnabled] = useState(formSchema.settings.resultsAccessControl?.enabled || false);
  const [resultsMethod, setResultsMethod] = useState<'password' | 'user_password' | 'email_otp'>(
    formSchema.settings.resultsAccessControl?.method || 'password'
  );
  const [resultsPassword, setResultsPassword] = useState('');
  const [resultsCredentials, setResultsCredentials] = useState<Array<{ username: string; passwordHash: string }>>(
    formSchema.settings.resultsAccessControl?.credentials || []
  );
  const [resultsEmails, setResultsEmails] = useState<string[]>(formSchema.settings.resultsAccessControl?.allowedEmails || []);
  const [resultsTitle, setResultsTitle] = useState(formSchema.settings.resultsAccessControl?.accessTitle || '');
  const [resultsMessage, setResultsMessage] = useState(formSchema.settings.resultsAccessControl?.accessMessage || '');
  const [newResultsEmail, setNewResultsEmail] = useState('');
  const [newResultsUsername, setNewResultsUsername] = useState('');
  const [newResultsUserPassword, setNewResultsUserPassword] = useState('');

  useEffect(() => {
    if (isOpen) {
      setDirection(formSchema.settings.direction || 'ltr');
      setSubtitle(formSchema.settings.subtitle || '');
      setInstructions(formSchema.settings.instructions || '');
      setSubmitButtonText(formSchema.settings.submitButtonText || 'Submit');
      setSuccessMessage(formSchema.settings.successMessage || 'Thank you for your submission!');
      setRedirectUrl(formSchema.settings.redirectUrl || '');
      setLogo(formSchema.settings.logo || '');
      setFavicon(formSchema.settings.favicon || '');
    }
  }, [isOpen, formSchema.settings]);

  const handleFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const base64 = await handleFileToBase64(file);
      setLogo(base64);
    }
  };

  const handleFaviconUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const base64 = await handleFileToBase64(file);
      setFavicon(base64);
    }
  };

  const handleSave = () => {
    updateSettings({
      direction,
      subtitle,
      instructions,
      submitButtonText,
      successMessage,
      redirectUrl: redirectUrl || undefined,
      logo: logo || undefined,
      favicon: favicon || undefined,
      accessControl: accessControlEnabled
        ? {
            enabled: true,
            method: accessMethod,
            password: accessMethod === 'password' ? accessPassword : undefined,
            passwords: accessMethod === 'multi_password' ? accessPasswords : undefined,
            credentials: accessMethod === 'user_password' ? accessCredentials : undefined,
            allowedEmails: accessMethod === 'email_otp' ? accessEmails : undefined,
            otpExpiryMinutes: accessMethod === 'email_otp' ? 10 : undefined,
            accessTitle: accessTitle || undefined,
            accessMessage: accessMessage || undefined,
          }
        : undefined,
      resultsAccessControl: resultsAccessEnabled
        ? {
            enabled: true,
            method: resultsMethod,
            password: resultsMethod === 'password' ? resultsPassword : undefined,
            credentials: resultsMethod === 'user_password' ? resultsCredentials : undefined,
            allowedEmails: resultsMethod === 'email_otp' ? resultsEmails : undefined,
            otpExpiryMinutes: resultsMethod === 'email_otp' ? 10 : undefined,
            accessTitle: resultsTitle || undefined,
            accessMessage: resultsMessage || undefined,
          }
        : undefined,
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75"
          onClick={onClose}
        ></div>

        {/* Modal panel */}
        <div
          dir={isRTL ? 'rtl' : 'ltr'}
          className={`inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg ${isRTL ? 'text-right' : 'text-left'} overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full`}
        >
          {/* Header */}
          <div className="bg-white dark:bg-gray-800 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('formSettings.title')}
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="bg-white dark:bg-gray-800 px-6 py-4 space-y-4 max-h-[60vh] overflow-y-auto">
            {/* Direction */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('formSettings.direction')}
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setDirection('ltr')}
                  className={`px-4 py-3 rounded-lg border-2 transition-all ${
                    direction === 'ltr'
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                      : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                    </svg>
                    <span className="font-medium">{t('formSettings.ltr')}</span>
                  </div>
                  <p className="text-xs mt-1 opacity-75">{t('formSettings.ltrHelp')}</p>
                </button>
                <button
                  type="button"
                  onClick={() => setDirection('rtl')}
                  className={`px-4 py-3 rounded-lg border-2 transition-all ${
                    direction === 'rtl'
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                      : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                    </svg>
                    <span className="font-medium">{t('formSettings.rtl')}</span>
                  </div>
                  <p className="text-xs mt-1 opacity-75">{t('formSettings.rtlHelp')}</p>
                </button>
              </div>
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                {t('formSettings.directionHelp')}
              </p>
            </div>

            {/* Subtitle */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('formSettings.subtitle')}
              </label>
              <input
                type="text"
                value={subtitle}
                onChange={(e) => setSubtitle(e.target.value)}
                placeholder={t('formSettings.subtitlePlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {t('formSettings.subtitleHelp')}
              </p>
            </div>

            {/* Instructions */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('formSettings.instructions')}
              </label>
              <textarea
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                placeholder={t('formSettings.instructionsPlaceholder')}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {t('formSettings.instructionsHelp')}
              </p>
            </div>

            {/* Submit Button Text */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('formSettings.submitButtonText')}
              </label>
              <input
                type="text"
                value={submitButtonText}
                onChange={(e) => setSubmitButtonText(e.target.value)}
                placeholder={t('formSettings.submitButtonPlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            {/* Success Message */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('formSettings.successMessage')}
              </label>
              <textarea
                value={successMessage}
                onChange={(e) => setSuccessMessage(e.target.value)}
                placeholder={t('formSettings.successMessagePlaceholder')}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            {/* Logo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('formSettings.logo')}
              </label>
              <div className="space-y-3">
                {/* Toggle between URL and Upload */}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setLogoInputType('url')}
                    className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                      logoInputType === 'url'
                        ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {t('formSettings.logoUrl')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setLogoInputType('upload')}
                    className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                      logoInputType === 'upload'
                        ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {t('formSettings.logoUpload')}
                  </button>
                </div>

                {logoInputType === 'url' ? (
                  <input
                    type="url"
                    value={logo}
                    onChange={(e) => setLogo(e.target.value)}
                    placeholder={t('formSettings.logoUrlPlaceholder')}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  />
                ) : (
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  />
                )}

                {logo && (
                  <div className="flex items-center gap-2">
                    <img src={logo} alt="Logo preview" className="h-12 w-auto object-contain" />
                    <button
                      type="button"
                      onClick={() => setLogo('')}
                      className="text-xs text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                    >
                      {t('formSettings.logoRemove')}
                    </button>
                  </div>
                )}
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {t('formSettings.logoHelp')}
              </p>
            </div>

            {/* Favicon */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('formSettings.favicon')}
              </label>
              <div className="space-y-3">
                {/* Toggle between URL and Upload */}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setFaviconInputType('url')}
                    className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                      faviconInputType === 'url'
                        ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {t('formSettings.faviconUrl')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setFaviconInputType('upload')}
                    className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                      faviconInputType === 'upload'
                        ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {t('formSettings.faviconUpload')}
                  </button>
                </div>

                {faviconInputType === 'url' ? (
                  <input
                    type="url"
                    value={favicon}
                    onChange={(e) => setFavicon(e.target.value)}
                    placeholder={t('formSettings.faviconUrlPlaceholder')}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  />
                ) : (
                  <input
                    type="file"
                    accept="image/x-icon,image/png,image/svg+xml"
                    onChange={handleFaviconUpload}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  />
                )}

                {favicon && (
                  <div className="flex items-center gap-2">
                    <img src={favicon} alt="Favicon preview" className="h-8 w-8 object-contain" />
                    <button
                      type="button"
                      onClick={() => setFavicon('')}
                      className="text-xs text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                    >
                      {t('formSettings.faviconRemove')}
                    </button>
                  </div>
                )}
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {t('formSettings.faviconHelp')}
              </p>
            </div>

            {/* Redirect URL */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('formSettings.redirectUrl')}
              </label>
              <input
                type="url"
                value={redirectUrl}
                onChange={(e) => setRedirectUrl(e.target.value)}
                placeholder={t('formSettings.redirectUrlPlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {t('formSettings.redirectUrlHelp')}
              </p>
            </div>

            {/* Access Control Section */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">{t('formSettings.accessControl.title')}</h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {t('formSettings.accessControl.description')}
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={accessControlEnabled}
                    onChange={(e) => setAccessControlEnabled(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                </label>
              </div>

              {accessControlEnabled && (
                <div className="space-y-4 pl-4 border-l-2 border-blue-500">
                  {/* Method Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('formSettings.accessControl.authMethod')}
                    </label>
                    <select
                      value={accessMethod}
                      onChange={(e) => setAccessMethod(e.target.value as any)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="password">{t('formSettings.accessControl.singlePassword')}</option>
                      <option value="multi_password">{t('formSettings.accessControl.multiplePasswords')}</option>
                      <option value="user_password">{t('formSettings.accessControl.usernamePassword')}</option>
                      <option value="email_otp">{t('formSettings.accessControl.emailOtp')}</option>
                    </select>
                  </div>

                  {/* Single Password */}
                  {accessMethod === 'password' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('formSettings.accessControl.password')}
                      </label>
                      <input
                        type="text"
                        value={accessPassword}
                        onChange={(e) => setAccessPassword(e.target.value)}
                        placeholder={t('formSettings.accessControl.passwordPlaceholder')}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                  )}

                  {/* Multiple Passwords */}
                  {accessMethod === 'multi_password' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('formSettings.accessControl.allowedPasswords')}
                      </label>
                      <div className="space-y-2">
                        {accessPasswords.map((pwd, idx) => (
                          <div key={idx} className="flex gap-2">
                            <input
                              type="text"
                              value={pwd}
                              readOnly
                              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white"
                            />
                            <button
                              onClick={() => setAccessPasswords(accessPasswords.filter((_, i) => i !== idx))}
                              className="px-3 py-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg"
                            >
                              {t('formSettings.accessControl.remove')}
                            </button>
                          </div>
                        ))}
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder={t('formSettings.accessControl.addPasswordPlaceholder')}
                            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                          <button
                            onClick={() => {
                              if (newPassword) {
                                setAccessPasswords([...accessPasswords, newPassword]);
                                setNewPassword('');
                              }
                            }}
                            className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                          >
                            {t('formSettings.accessControl.add')}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* User + Password */}
                  {accessMethod === 'user_password' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('formSettings.accessControl.userCredentials')}
                      </label>
                      <div className="space-y-2">
                        {accessCredentials.map((cred, idx) => (
                          <div key={idx} className="flex gap-2 items-center">
                            <input
                              type="text"
                              value={cred.username}
                              readOnly
                              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white"
                              placeholder={t('formSettings.accessControl.usernamePlaceholder')}
                            />
                            <span className="text-gray-500">•••••••</span>
                            <button
                              onClick={() => setAccessCredentials(accessCredentials.filter((_, i) => i !== idx))}
                              className="px-3 py-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg"
                            >
                              {t('formSettings.accessControl.remove')}
                            </button>
                          </div>
                        ))}
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={newUsername}
                            onChange={(e) => setNewUsername(e.target.value)}
                            placeholder={t('formSettings.accessControl.usernamePlaceholder')}
                            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                          <input
                            type="password"
                            value={newUserPassword}
                            onChange={(e) => setNewUserPassword(e.target.value)}
                            placeholder={t('formSettings.accessControl.passwordFieldPlaceholder')}
                            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                          <button
                            onClick={() => {
                              if (newUsername && newUserPassword) {
                                setAccessCredentials([...accessCredentials, { username: newUsername, passwordHash: newUserPassword }]);
                                setNewUsername('');
                                setNewUserPassword('');
                              }
                            }}
                            className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                          >
                            {t('formSettings.accessControl.add')}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Email OTP */}
                  {accessMethod === 'email_otp' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('formSettings.accessControl.allowedEmails')}
                      </label>
                      <div className="space-y-2">
                        {accessEmails.map((email, idx) => (
                          <div key={idx} className="flex gap-2">
                            <input
                              type="email"
                              value={email}
                              readOnly
                              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white"
                            />
                            <button
                              onClick={() => setAccessEmails(accessEmails.filter((_, i) => i !== idx))}
                              className="px-3 py-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg"
                            >
                              {t('formSettings.accessControl.remove')}
                            </button>
                          </div>
                        ))}
                        <div className="flex gap-2">
                          <input
                            type="email"
                            value={newEmail}
                            onChange={(e) => setNewEmail(e.target.value)}
                            placeholder={t('formSettings.accessControl.addEmailPlaceholder')}
                            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                          <button
                            onClick={() => {
                              if (newEmail) {
                                setAccessEmails([...accessEmails, newEmail]);
                                setNewEmail('');
                              }
                            }}
                            className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                          >
                            {t('formSettings.accessControl.add')}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Custom Messages */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('formSettings.accessControl.customTitle')}
                    </label>
                    <input
                      type="text"
                      value={accessTitle}
                      onChange={(e) => setAccessTitle(e.target.value)}
                      placeholder={t('formSettings.accessControl.customTitlePlaceholder')}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('formSettings.accessControl.customMessage')}
                    </label>
                    <textarea
                      value={accessMessage}
                      onChange={(e) => setAccessMessage(e.target.value)}
                      placeholder={t('formSettings.accessControl.customMessagePlaceholder')}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Results Access Control Section */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">{t('formSettings.resultsAccessControl.title')}</h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {t('formSettings.resultsAccessControl.description')}
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={resultsAccessEnabled}
                    onChange={(e) => setResultsAccessEnabled(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                </label>
              </div>

              {resultsAccessEnabled && (
                <div className="space-y-4 pl-4 border-l-2 border-green-500">
                  {/* Method Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('formSettings.accessControl.authMethod')}
                    </label>
                    <select
                      value={resultsMethod}
                      onChange={(e) => setResultsMethod(e.target.value as any)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="password">{t('formSettings.accessControl.singlePassword')}</option>
                      <option value="user_password">{t('formSettings.accessControl.usernamePassword')}</option>
                      <option value="email_otp">{t('formSettings.accessControl.emailOtp')}</option>
                    </select>
                  </div>

                  {/* Single Password */}
                  {resultsMethod === 'password' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('formSettings.accessControl.password')}
                      </label>
                      <input
                        type="text"
                        value={resultsPassword}
                        onChange={(e) => setResultsPassword(e.target.value)}
                        placeholder={t('formSettings.resultsAccessControl.passwordPlaceholder')}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                  )}

                  {/* User + Password */}
                  {resultsMethod === 'user_password' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('formSettings.accessControl.userCredentials')}
                      </label>
                      <div className="space-y-2">
                        {resultsCredentials.map((cred, idx) => (
                          <div key={idx} className="flex gap-2 items-center">
                            <input
                              type="text"
                              value={cred.username}
                              readOnly
                              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white"
                              placeholder={t('formSettings.accessControl.usernamePlaceholder')}
                            />
                            <span className="text-gray-500">•••••••</span>
                            <button
                              onClick={() => setResultsCredentials(resultsCredentials.filter((_, i) => i !== idx))}
                              className="px-3 py-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg"
                            >
                              {t('formSettings.accessControl.remove')}
                            </button>
                          </div>
                        ))}
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={newResultsUsername}
                            onChange={(e) => setNewResultsUsername(e.target.value)}
                            placeholder={t('formSettings.accessControl.usernamePlaceholder')}
                            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                          <input
                            type="password"
                            value={newResultsUserPassword}
                            onChange={(e) => setNewResultsUserPassword(e.target.value)}
                            placeholder={t('formSettings.accessControl.passwordFieldPlaceholder')}
                            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                          <button
                            onClick={() => {
                              if (newResultsUsername && newResultsUserPassword) {
                                setResultsCredentials([...resultsCredentials, { username: newResultsUsername, passwordHash: newResultsUserPassword }]);
                                setNewResultsUsername('');
                                setNewResultsUserPassword('');
                              }
                            }}
                            className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                          >
                            {t('formSettings.accessControl.add')}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Email OTP */}
                  {resultsMethod === 'email_otp' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('formSettings.accessControl.allowedEmails')}
                      </label>
                      <div className="space-y-2">
                        {resultsEmails.map((email, idx) => (
                          <div key={idx} className="flex gap-2">
                            <input
                              type="email"
                              value={email}
                              readOnly
                              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white"
                            />
                            <button
                              onClick={() => setResultsEmails(resultsEmails.filter((_, i) => i !== idx))}
                              className="px-3 py-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg"
                            >
                              {t('formSettings.accessControl.remove')}
                            </button>
                          </div>
                        ))}
                        <div className="flex gap-2">
                          <input
                            type="email"
                            value={newResultsEmail}
                            onChange={(e) => setNewResultsEmail(e.target.value)}
                            placeholder={t('formSettings.accessControl.addEmailPlaceholder')}
                            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                          <button
                            onClick={() => {
                              if (newResultsEmail) {
                                setResultsEmails([...resultsEmails, newResultsEmail]);
                                setNewResultsEmail('');
                              }
                            }}
                            className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                          >
                            {t('formSettings.accessControl.add')}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Custom Messages */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('formSettings.accessControl.customTitle')}
                    </label>
                    <input
                      type="text"
                      value={resultsTitle}
                      onChange={(e) => setResultsTitle(e.target.value)}
                      placeholder={t('formSettings.resultsAccessControl.customTitlePlaceholder')}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('formSettings.accessControl.customMessage')}
                    </label>
                    <textarea
                      value={resultsMessage}
                      onChange={(e) => setResultsMessage(e.target.value)}
                      placeholder={t('formSettings.resultsAccessControl.customMessagePlaceholder')}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 dark:bg-gray-900 px-6 py-4 flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              {t('actions.cancel', { ns: 'common' })}
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
            >
              {t('actions.save', { ns: 'common' })}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
